
import Vue from "vue";
import moment from "moment";
import { db } from "@/utils/dexie";
import eventBus from "@/eventBus";
import SummaryCards from "./summary/SummaryCards.vue";
import {
  loadPgFeeRates,
  detectUnconfiguredCombinations,
  summarizePgFees,
  PgFeeSummary,
} from "@/utils/pgFee";
import { summarizePaymentCard } from "@/utils/paymentCardSummary";
import {
  filterSettlementResult,
  OrderPathFilter,
} from "@/utils/orderPathFilter";
import { getSettlementPeriodType } from "@/utils/settlementPeriod";
import {
  formatSettledElapsedTime,
  SettledElapsedTimeInfo,
} from "@/utils/settlementTimeTracker";

export default Vue.extend({
  name: "ResultSummary",
  components: {
    SummaryCards,
  },
  props: {
    loading: { type: Boolean, default: false },
    last_settled_at: { type: Number, default: undefined },
    start_date: String,
    end_date: String,
    action_method: Function,
    action_detail_method: Function,
    show_detail_button: { type: Boolean, default: true },
    action_icon: String,
    action_name: String,
    action_disabled: Boolean,
    order_count: { type: Number, default: 0 },
    payment_composition_amount: { type: Number, default: 0 },
    refund_count: { type: Number, default: 0 },
    refund_amount: { type: Number, default: 0 },
    refund_breakdown: {
      type: Object as () => Record<string, number>,
      default: () => ({}),
    },
    total_amount: { type: Number, default: 0 },
    fee_summary: {
      type: Object as () => PgFeeSummary,
      default: undefined,
    },
    payment_summary: {
      type: Object as () => Record<string, number>,
      default: undefined,
    },
    orders: {
      type: Array as () => any[],
      default: () => [],
    },
    refunds: {
      type: Array as () => any[],
      default: () => [],
    },
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data() {
    return {
      prev_flag: false,
      prev_order_count: 0,
      prev_payment_composition_amount: 0,
      prev_refund_count: 0,
      prev_refund_amount: 0,
      isFetchingPrev: false,
      isCheckingPrev: true,
      unconfiguredCombos: [] as any[],
      prev_fee_summary: undefined as PgFeeSummary | undefined,
      timeNow: Date.now(),
      timeTimer: null as any,
    };
  },
  computed: {
    settledInfo(): SettledElapsedTimeInfo {
      return formatSettledElapsedTime(this.last_settled_at, this.timeNow);
    },
    paymentCardSummary(): Record<string, number> {
      return {
        ...(this.payment_summary || {}),
        ...summarizePaymentCard(this.orders),
      };
    },
    unconfiguredCombosSummary(): string {
      if (!this.unconfiguredCombos || this.unconfiguredCombos.length === 0)
        return "";
      const firstTwo = this.unconfiguredCombos
        .slice(0, 2)
        .map((item) => `${item.pathName}(${item.methodName})`)
        .join(", ");
      if (this.unconfiguredCombos.length > 2) {
        return `${firstTwo} 외 ${this.unconfiguredCombos.length - 2}건`;
      }
      return firstTwo;
    },
    refundRate(): number {
      const order = this.payment_composition_amount || 0;
      const refund = Math.abs(this.refund_amount || 0);
      if (!order) return 0;
      return Math.min(100, (refund / order) * 100);
    },
    formattedRefundRate(): string {
      return this.refundRate.toFixed(1);
    },
    refundRateColor(): string {
      if (this.refundRate < 10) return "success";
      if (this.refundRate < 20) return "warning";
      return "error";
    },
    refundRateBadgeClass(): string {
      if (this.refundRate < 10) return "badge-blue";
      if (this.refundRate < 20) return "badge-warning";
      return "badge-red";
    },
    refundRateStatus(): string {
      if (this.refundRate < 10) return "양호";
      if (this.refundRate < 20) return "보통";
      return "주의";
    },
    compare_amount(): number {
      const currentTotal = this.total_amount || 0;
      const prevTotal =
        this.prev_payment_composition_amount + this.prev_refund_amount;
      return currentTotal - prevTotal;
    },
    compare_payment_composition_amount(): number {
      return (
        (this.payment_composition_amount || 0) -
        this.prev_payment_composition_amount
      );
    },
    compare_refund_amount(): number {
      return (this.refund_amount || 0) - this.prev_refund_amount;
    },
    summaryType(): string {
      return getSettlementPeriodType(this.start_date, this.end_date);
    },
    previousRange(): { start: string; end: string } | null {
      const type = this.summaryType;
      const format = (d: moment.Moment) => d.format("YYYY-MM-DD");

      if (type === "d") {
        const yest = moment(this.start_date).subtract(1, "day");
        return { start: format(yest), end: format(yest) };
      }

      if (type === "w") {
        const start = moment(this.start_date).subtract(7, "days");
        const end = moment(start).add(6, "days");
        return { start: format(start), end: format(end) };
      }

      if (type === "m") {
        const currentStart = moment(this.start_date);
        const prevMonthStart = currentStart
          .clone()
          .subtract(1, "month")
          .startOf("month");
        const prevMonthEnd = prevMonthStart.clone().endOf("month");
        return { start: format(prevMonthStart), end: format(prevMonthEnd) };
      }

      return { start: "", end: "" };
    },
    previousPeriodName(): string {
      const type = this.summaryType;
      if (type === "d") return "전일";
      if (type === "w") return "전주";
      if (type === "m") return "전월";
      return "이전 기간";
    },
    previousPeriodLabel(): string {
      if (!this.previousRange || !this.previousRange.start) return "";
      const start = moment(this.previousRange.start);
      const end = moment(this.previousRange.end);
      const type = this.summaryType;

      if (type === "d") {
        return `전일(${start.format("MM.DD")})`;
      }
      if (type === "w") {
        return `전주(${start.format("MM.DD")}~${end.format("MM.DD")})`;
      }
      if (type === "m") {
        return `전월(${start.format("YYYY.MM")})`;
      }
      return `이전 기간(${start.format("MM.DD")}~${end.format("MM.DD")})`;
    },
    isPrevFuture(): boolean {
      if (!this.previousRange || !this.previousRange.start) return false;
      return moment(this.previousRange.start).isAfter(moment(), "day");
    },
    isCurrentPeriodEmpty(): boolean {
      return (
        (this.order_count || 0) === 0 &&
        (this.payment_composition_amount || 0) === 0 &&
        (this.refund_count || 0) === 0 &&
        (this.refund_amount || 0) === 0
      );
    },
    isPreviousPeriodEmpty(): boolean {
      return (
        this.prev_flag &&
        (this.prev_order_count || 0) === 0 &&
        (this.prev_payment_composition_amount || 0) === 0 &&
        (this.prev_refund_count || 0) === 0 &&
        (this.prev_refund_amount || 0) === 0
      );
    },
    periodLabel(): string {
      if (!this.start_date || !this.end_date) return "선택 기간";
      const start = moment(this.start_date);
      const end = moment(this.end_date);
      if (start.isSame(end, "day")) {
        return start.format("YYYY.MM.DD");
      }
      return `${start.format("YYYY.MM.DD")} ~ ${end.format("YYYY.MM.DD")}`;
    },
  },
  watch: {
    previousRange: {
      handler() {
        this.fetchPreviousData();
      },
      immediate: true,
      deep: true,
    },
    start_date: {
      immediate: true,
      handler() {
        this.checkUnconfiguredCombos();
      },
    },
    end_date() {
      this.checkUnconfiguredCombos();
    },
    orderPathFilter: {
      handler() {
        this.fetchPreviousData();
        this.checkUnconfiguredCombos();
      },
      deep: true,
    },
  },
  methods: {
    emitDetail() {
      if (typeof this.action_detail_method === "function") {
        this.action_detail_method(this.start_date, this.end_date);
      } else {
        this.$emit("open-detail", this.start_date, this.end_date);
        eventBus.$emit("open-detail", this.start_date, this.end_date);
      }
    },
    emitQuery() {
      if (this.settledInfo.isJustSettled) {
        eventBus.$emit("snackbar", {
          message:
            "방금 정산을 완료한 최신 데이터입니다. 약 1분 후 다시 정산할 수 있습니다.",
        });
        return;
      }
      if (typeof this.action_method === "function") {
        this.action_method();
      } else {
        eventBus.$emit("query", this.start_date, this.end_date, true);
      }
    },
    async triggerPreviousSettlement() {
      if (
        !this.previousRange ||
        !this.previousRange.start ||
        this.isFetchingPrev
      )
        return;

      this.isFetchingPrev = true;
      eventBus.$emit("snackbar", {
        message: `${this.previousPeriodName} 백그라운드 정산을 시작합니다. Cafe24에서 데이터를 가져와 정산 완료 후 비교 수치가 업데이트됩니다.`,
      });

      eventBus.$emit(
        "query",
        this.previousRange.start,
        this.previousRange.end,
        true,
        false
      );

      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        await this.fetchPreviousData();
        if (this.prev_flag || attempts >= 20) {
          clearInterval(interval);
          this.isFetchingPrev = false;
          if (this.prev_flag) {
            eventBus.$emit("snackbar", {
              message: `✅ ${this.previousPeriodName} 정산 완료! 증감 비교 수치가 업데이트되었습니다.`,
            });
          }
        }
      }, 1000);
    },
    async fetchPreviousData() {
      this.isCheckingPrev = true;
      this.prev_flag = false;
      this.prev_payment_composition_amount = 0;
      if (!this.previousRange || !this.previousRange.start) {
        this.isCheckingPrev = false;
        return;
      }
      this.isFetchingPrev = true;

      try {
        const mallId = this.$store.state.mall_info?.mall_id;
        const shopNo = this.$store.state.mall_info?.shop_no;
        if (!mallId || !shopNo) {
          this.isCheckingPrev = false;
          return;
        }

        const start = moment(this.previousRange.start);
        const end = moment(this.previousRange.end);
        const startDateStr = start.format("YYYY-MM-DD");
        const endDateStr = end.format("YYYY-MM-DD");

        const dayCount = end.diff(start, "days") + 1;

        const rows = await db.results
          .where("[mall_id+shop_no+date]")
          .between(
            [mallId, shopNo, startDateStr],
            [mallId, shopNo, endDateStr],
            true,
            true
          )
          .toArray();

        let paymentCompositionAmount = 0;
        let refund_amount = 0;
        let order_count = 0;
        let refund_count = 0;
        const previousItems: any[] = [];

        if (rows.length === dayCount) {
          for (const row of rows) {
            const filtered = filterSettlementResult(row, this.orderPathFilter);
            paymentCompositionAmount += filtered.payment_composition_amount;
            refund_amount += filtered.refund_amount;
            order_count += filtered.order_count;
            refund_count += filtered.refund_count;
            if (Array.isArray(filtered.orders)) {
              previousItems.push(...filtered.orders);
            }
            if (Array.isArray(filtered.refunds))
              previousItems.push(...filtered.refunds);
          }
          this.prev_flag = true;
        }

        this.prev_payment_composition_amount = this.prev_flag
          ? paymentCompositionAmount
          : 0;
        this.prev_refund_amount = refund_amount;
        this.prev_order_count = order_count;
        this.prev_refund_count = refund_count;
        this.prev_fee_summary = this.prev_flag
          ? summarizePgFees(
              previousItems,
              loadPgFeeRates(),
              this.orderPathFilter?.presentationMode
            )
          : undefined;
      } catch (e) {
        console.error("fetchPreviousData error:", e);
      } finally {
        this.isFetchingPrev = false;
        this.isCheckingPrev = false;
      }
    },
    async checkUnconfiguredCombos() {
      if (!this.start_date || !this.end_date) return;
      try {
        const mallId = this.$store.state.mall_info?.mall_id;
        const shopNo = this.$store.state.mall_info?.shop_no;
        if (!mallId || !shopNo) return;

        const rows = await db.results
          .where("[mall_id+shop_no+date]")
          .between(
            [mallId, shopNo, this.start_date],
            [mallId, shopNo, this.end_date],
            true,
            true
          )
          .toArray();

        let allItems: any[] = [];
        for (const row of rows) {
          const filtered = filterSettlementResult(row, this.orderPathFilter);
          if (Array.isArray(filtered.orders)) allItems.push(...filtered.orders);
          if (Array.isArray(filtered.refunds))
            allItems.push(...filtered.refunds);
        }

        const pgRates = loadPgFeeRates();
        this.unconfiguredCombos = detectUnconfiguredCombinations(
          allItems,
          pgRates,
          this.orderPathFilter?.presentationMode
        );
      } catch (e) {
        console.error("checkUnconfiguredCombos error:", e);
      }
    },
    openUnconfiguredFeeModal() {
      eventBus.$emit(
        "open-pg-fee-settings",
        this.unconfiguredCombos,
        "unconfigured",
        this.orderPathFilter?.presentationMode
      );
    },
  },
  created() {
    eventBus.$on("pg-fee-updated", this.checkUnconfiguredCombos);
  },
  mounted() {
    this.timeTimer = setInterval(() => {
      this.timeNow = Date.now();
    }, 10000);
  },
  beforeDestroy() {
    eventBus.$off("pg-fee-updated", this.checkUnconfiguredCombos);
    if (this.timeTimer) {
      clearInterval(this.timeTimer);
      this.timeTimer = null;
    }
  },
});
